import $$ from 'dom7';

/*
 * Transaction history and transaction details.
 *
 *   /transaction/wallet      the wallet ledger   lockers.php?transactionHistory
 *   /transaction/payment     locker payments     lockers.php?paymentHistory
 *   /transaction/withdrawal  investment payouts  invest.php  withdrawalHistory
 *   /txn/:key/               one transaction, in full, with Repeat
 *
 * Every row from the API is turned into one view model (walletModel() and
 * friends) that both the list and the details page draw from, so the two can
 * never disagree about a title, a sign or a status.
 *
 * The list is kept in memory, per session token: Back from the details page
 * rebuilds the list (the router does not keep previous pages), and redrawing
 * it from memory keeps the customer's filter and scroll position instead of
 * dropping them at the top of a reloaded list. It refreshes quietly behind.
 */

/* -------------------------------------------------------------------------
   Formatting
   ------------------------------------------------------------------------- */

var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function pad(n) {
  return (n < 10 ? '0' : '') + n;
}

/* The API's dates are "30/09/2026 14:05:11"; anything else is shown as sent.
   Non-breaking spaces inside the date and the time, so a narrow row breaks
   between them and never leaves "AM" alone on a line. */
var NBSP = ' ';
function niceDate(text, withSeconds) {
  var m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?/.exec(String(text || '').trim());
  if (!m || +m[2] < 1 || +m[2] > 12) return String(text || '');
  var out = +m[1] + NBSP + MONTHS[+m[2] - 1] + NBSP + m[3];
  if (m[4] !== undefined) {
    var h = +m[4];
    out += ', ' + (h % 12 || 12) + ':' + m[5] + (withSeconds && m[6] ? ':' + m[6] : '') + NBSP + (h < 12 ? 'AM' : 'PM');
  }
  return out;
}

function statusOf(code) {
  switch (String(code)) {
    case '0': return { label: 'Pending', kind: 'pending' };
    case '1': return { label: 'Success', kind: 'success' };
    /* A recharge the operator accepted with no final result yet. "Failed"
       would promise a refund for something that may well have been delivered. */
    case '3': return { label: 'Processing', kind: 'pending' };
    default: return { label: 'Failed', kind: 'failed' };
  }
}

function isUrl(value) {
  return typeof value === 'string' && /^https?:\/\//i.test(value);
}

function filled(value) {
  return value !== null && value !== undefined && String(value).trim() !== '' && String(value) !== 'N/A';
}

/* -------------------------------------------------------------------------
   Repeat: a short-lived handoff to the screen that does the transaction
   ------------------------------------------------------------------------- */

var handoff = null;
var HANDOFF_MS = 10 * 60 * 1000;

function giveRepeat(target, data) {
  handoff = { target: target, data: data, at: Date.now() };
}

/*
 * Called by the recharge, transfer, withdraw and deposit screens on init.
 * Returns what to prefill once, then forgets it; a handoff the customer
 * walked away from expires rather than surprising them later.
 */
export function takeRepeat(target) {
  var h = handoff;
  if (!h || h.target !== target) return null;
  handoff = null;
  return Date.now() - h.at > HANDOFF_MS ? null : h.data;
}

/* -------------------------------------------------------------------------
   View models
   ------------------------------------------------------------------------- */

/*
 * Wallet-to-wallet rows name the other customer (`walletTransferUid` carries
 * the name, despite its key). The API does not yet send their picture or
 * phone; these are the fields the app reads when it does -- the picture for
 * the row, the phone for "Send again".
 */
var PEER_IMAGE = 'walletTransferImg';
var PEER_PHONE = 'walletTransferPhone';

/* Filters on the wallet list, in display order. `types` are API txn types. */
var GROUPS = [
  { id: 'added', label: 'Added', types: ['1', '9'] },
  { id: 'withdrawn', label: 'Withdrawn', types: ['2'] },
  { id: 'sent', label: 'Sent', types: ['5'] },
  { id: 'received', label: 'Received', types: ['6'] },
  { id: 'recharge', label: 'Recharges', types: ['7', '8'] },
  { id: 'rewards', label: 'Refunds & cashback', types: ['3', '4'] },
];

function groupOf(type) {
  for (var i = 0; i < GROUPS.length; i += 1) {
    if (GROUPS[i].types.indexOf(type) !== -1) return GROUPS[i].id;
  }
  return 'rewards';
}

/*
 * Money that did not move has no direction: a failed deposit is not "+₹500"
 * in green, it is ₹500 that never arrived. Sign and colour are for money that
 * moved, or is on its way.
 */
function amountFor(fmt, amount, flow, status) {
  var text = fmt.format(+amount || 0);
  if (status.kind === 'failed' || !flow || flow === 'neutral') return { text: text, flow: 'neutral' };
  return { text: (flow === 'in' ? '+' : '−') + text, flow: flow };
}

function feeFor(item, fmt) {
  if (!(+item.withdrawalFee > 0)) return null;
  var fee = { amount: fmt.format(+item.withdrawalFee), txnID: item.txnID };
  var state = String(item.feeStatus);
  if (state === '1') {
    fee.state = 'Paid'; fee.kind = 'success';
  } else if (state === '2') {
    fee.state = 'Rejected'; fee.kind = 'failed'; fee.payable = true;
  } else if (filled(item.utr) && String(item.utr) !== '0') {
    fee.state = 'Payment being verified'; fee.kind = 'pending';
  } else {
    fee.state = 'Not paid'; fee.kind = 'failed'; fee.payable = true;
  }
  return fee;
}

function walletModel(item, fmt) {
  var type = String(item.type);
  var status = statusOf(item.status);
  var peer = filled(item.walletTransferUid) ? String(item.walletTransferUid) : '';
  var rc = item.rechargeDetails || {};
  var m = {
    key: 'w' + item.id,
    list: 'wallet',
    type: type,
    group: groupOf(type),
    status: status,
    date: item.date,
    rows: [],
  };
  var flow = 'in';

  switch (type) {
    case '1':
      m.title = 'Money deposited';
      m.kind = 'in';
      m.repeat = { target: 'deposit', label: 'Deposit again', route: '/deposit/', data: { amount: item.amount } };
      break;
    case '9':
      /* An approved Credit for Business, as first recorded; newer ones are type 1 */
      m.title = 'Money added';
      m.kind = 'in';
      break;
    case '2':
      m.title = 'Money withdrawn';
      m.kind = 'out';
      flow = 'out';
      m.fee = feeFor(item, fmt);
      m.repeat = { target: 'withdraw', label: 'Withdraw again', route: '/withdraw/', data: { amount: item.amount } };
      break;
    case '3':
      m.title = 'Money refunded';
      m.kind = 'refund';
      break;
    case '4':
      m.title = String(item.cashbackType) === '1' ? 'Welcome bonus received' : 'Cashback received';
      m.kind = 'cashback';
      break;
    case '5':
      m.title = peer ? 'Sent to ' + peer : 'Sent to another wallet';
      m.kind = 'transfer';
      flow = 'out';
      m.avatar = isUrl(item[PEER_IMAGE]) ? item[PEER_IMAGE] : '';
      m.repeat = {
        target: 'transfer', label: 'Send again', route: '/money-transfer/',
        data: { amount: item.amount, phone: filled(item[PEER_PHONE]) ? String(item[PEER_PHONE]) : '' },
      };
      if (peer) m.rows.push(['Sent to', peer]);
      break;
    case '6':
      m.title = peer ? 'Received from ' + peer : 'Received from another wallet';
      m.kind = 'transfer';
      m.avatar = isUrl(item[PEER_IMAGE]) ? item[PEER_IMAGE] : '';
      if (peer) m.rows.push(['Received from', peer]);
      break;
    case '7':
      m.title = (rc.serviceLabel || 'Recharge') + (rc.operator ? ' · ' + rc.operator : '');
      m.kind = 'recharge';
      flow = 'out';
      m.sub = rc.number || '';
      if (rc.service && rc.number) {
        m.repeat = {
          target: 'recharge',
          label: rc.service === 'postpaid' ? 'Pay again' : 'Repeat recharge',
          route: '/recharge/' + (rc.service === 'prepaid' ? 'mobile' : rc.service) + '/',
          data: { service: rc.service, operator: rc.operator || '', circle: rc.circle || '', number: rc.number, amount: item.amount },
        };
      }
      break;
    case '8':
      m.title = 'Refund · ' + (rc.serviceLabel || 'Recharge');
      m.kind = 'refund';
      m.sub = rc.number || '';
      break;
    default:
      m.title = 'Cashback received';
      m.kind = 'cashback';
  }

  var amount = amountFor(fmt, item.amount, flow, status);
  m.amount = amount.text;
  m.flow = amount.flow;

  if (type === '7' || type === '8') {
    if (rc.serviceLabel) m.rows.push(['Service', rc.serviceLabel]);
    if (rc.number) m.rows.push([rc.service === 'dth' ? 'Subscriber ID' : 'Number', rc.number]);
    if (rc.operator) m.rows.push(['Operator', rc.operator]);
    if (rc.circle) m.rows.push(['Circle', rc.circle]);
    if (type === '7') {
      if (+rc.walletAmount > 0) m.rows.push(['Paid from wallet', fmt.format(+rc.walletAmount)]);
      if (+rc.cashbackAmount > 0) m.rows.push(['Paid from cashback', fmt.format(+rc.cashbackAmount)]);
    }
  }
  if (filled(item.updatedBalance)) m.rows.push(['Wallet balance after', fmt.format(+item.updatedBalance)]);
  m.txnID = item.txnID || '';
  /* The server's own words on a failure (a rejection reason, a provider error) */
  if (status.kind === 'failed' && filled(item.remarks)) m.note = String(item.remarks);
  return m;
}

function paymentModel(item, i, fmt) {
  var status = statusOf(item.status);
  var m = {
    key: 'p' + (item.txnID || i),
    list: 'payment',
    title: String(item.type) === '1' ? 'Payment for subscription' : 'Payment for renewal',
    kind: 'payment',
    status: status,
    date: item.date,
    txnID: item.txnID || '',
    rows: [],
  };
  m.amount = fmt.format(+item.amount || 0);
  m.flow = 'neutral';
  return m;
}

function investModel(item, i, fmt) {
  var status = statusOf(item.status);
  var m = {
    key: 'i' + (item.txnID || i),
    list: 'withdrawal',
    title: 'Investment withdrawal',
    kind: 'out',
    status: status,
    date: item.date,
    txnID: item.txnID || '',
    sub: item.investID || '',
    rows: [],
  };
  var amount = amountFor(fmt, item.amount, 'out', status);
  m.amount = amount.text;
  m.flow = amount.flow;
  if (item.investID) m.rows.push(['Investment', String(item.investID)]);
  if (filled(item.updatedBalance)) m.rows.push(['Balance after', fmt.format(+item.updatedBalance)]);
  if (status.kind === 'failed' && filled(item.remarks)) m.note = String(item.remarks);
  return m;
}

/* -------------------------------------------------------------------------
   Data: fetch, and the in-memory copy the details page and Back draw from
   ------------------------------------------------------------------------- */

var SOURCES = {
  wallet: {
    title: 'Transaction History',
    empty: 'No transactions yet',
    fetch: function (apiBase, token) {
      return window.jQuery.getJSON(apiBase + 'app/php/lockers.php?transactionHistory=true&token=' + encodeURIComponent(token));
    },
    items: function (data) { return data.transactionHistory; },
    model: function (item, i, fmt) { return walletModel(item, fmt); },
  },
  payment: {
    title: 'Payment History',
    empty: 'No payments yet',
    fetch: function (apiBase, token) {
      return window.jQuery.getJSON(apiBase + 'app/php/lockers.php?paymentHistory=true&token=' + encodeURIComponent(token));
    },
    items: function (data) { return data.paymentHistory; },
    model: paymentModel,
  },
  withdrawal: {
    title: 'Withdrawal History',
    empty: 'No withdrawals yet',
    fetch: function (apiBase, token) {
      return window.jQuery.post(apiBase + 'app/php/invest.php', { withdrawalHistory: true, token: token });
    },
    items: function (data) { return data.withdrawHistory; },
    model: investModel,
  },
};

/* per list: { token, raw (JSON text), models, filter, scrollTop } */
var lists = {};
/* key -> model, across the lists, for /txn/:key/ */
var byKey = {};

/* As before: "wallet", "payment", and anything else is the investment list */
function sourceFor(type) {
  return SOURCES[type] ? type : 'withdrawal';
}

function remember(type, data, fmt) {
  var src = SOURCES[type];
  var items = data && data.status === 'success' && Array.isArray(src.items(data)) ? src.items(data) : [];
  var models = items.map(function (item, i) { return src.model(item, i, fmt); });
  models.forEach(function (m) { byKey[m.key] = m; });
  var state = lists[type] || {};
  state.token = localStorage.getItem('authToken');
  state.raw = JSON.stringify(items);
  state.models = models;
  lists[type] = state;
  return state;
}

function cachedList(type) {
  var state = lists[type];
  if (!state || state.token !== localStorage.getItem('authToken')) return null;
  return state;
}

/* -------------------------------------------------------------------------
   Drawing
   ------------------------------------------------------------------------- */

var ICONS = {
  in: '<path d="M12 4v11"/><path d="m8 11 4 4 4-4"/><path d="M4.5 19.5h15"/>',
  out: '<path d="M12 20V9"/><path d="m8 13 4-4 4 4"/><path d="M4.5 4.5h15"/>',
  refund: '<path d="M4 9h11a5 5 0 0 1 0 10h-6"/><path d="m8 5-4 4 4 4"/>',
  cashback: '<circle cx="12" cy="12" r="8.5"/><path d="M9.2 14.8 14.8 9.2"/><circle cx="9.6" cy="9.6" r="1.1" fill="currentColor" stroke="none"/><circle cx="14.4" cy="14.4" r="1.1" fill="currentColor" stroke="none"/>',
  payment: '<rect x="3" y="5.5" width="18" height="13" rx="2.5"/><path d="M3 10h18"/>',
  transfer: '<path d="M4 8.5h13"/><path d="M14 5.5l3 3-3 3"/><path d="M20 15.5H7"/><path d="M10 12.5l-3 3 3 3"/>',
  recharge: '<rect x="6.5" y="2.5" width="11" height="19" rx="2.5"/><path d="M10.75 18.5h2.5"/>',
};

function el(tag, cls, text) {
  var node = document.createElement(tag);
  if (cls) node.className = cls;
  if (text !== undefined && text !== null) node.textContent = text;
  return node;
}

/*
 * The other customer's picture on a transfer, when the API sends one; the
 * usual icon tile otherwise, or if the picture will not load.
 */
function tileFor(m, cls) {
  var tile = el('span', cls + ' ' + cls + '--' + (m.kind || 'payment'));
  tile.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">' +
    (ICONS[m.kind] || ICONS.payment) + '</svg>';
  if (m.avatar) {
    var img = el('img', cls + '__photo');
    img.alt = '';
    img.onerror = function () { img.remove(); tile.classList.remove('has-photo'); };
    img.src = m.avatar;
    tile.appendChild(img);
    tile.classList.add('has-photo');
  }
  return tile;
}

function statusPill(status, cls) {
  return el('span', cls + ' ' + cls + '--' + status.kind, status.label);
}

/*
 * One row. The title wraps to two lines instead of being cut off, and the
 * amount and status sit in their own column, so a large amount can no longer
 * push the status out of sight. The full story is one tap away.
 */
function rowFor(m) {
  var li = el('li', 'ypw-txn');
  var link = el('a', 'ypw-txn__link no-ripple');
  link.setAttribute('href', '/txn/' + encodeURIComponent(m.key) + '/');

  link.appendChild(tileFor(m, 'ypw-txn__tile'));

  var body = el('span', 'ypw-txn__body');
  body.appendChild(el('span', 'ypw-txn__title', m.title));
  /* One run of text, so it wraps between words, never before a "·" */
  body.appendChild(el('span', 'ypw-txn__meta', [m.sub, niceDate(m.date)].filter(Boolean).join(' · ')));
  if (m.fee && m.fee.payable) body.appendChild(el('span', 'ypw-txn__flag', 'Withdrawal fee due'));
  link.appendChild(body);

  var side = el('span', 'ypw-txn__side');
  side.appendChild(el('span', 'ypw-txn__amount ypw-txn__amount--' + m.flow, m.amount));
  side.appendChild(statusPill(m.status, 'ypw-txn__status'));
  link.appendChild(side);

  li.appendChild(link);
  return li;
}

function emptyState(title, text) {
  var box = el('section', 'ypw-empty');
  var art = el('span', 'ypw-empty__art');
  art.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3.5" y="4.5" width="17" height="15.5" rx="3"/><path d="M7.5 9.5h9M7.5 13h6"/></svg>';
  box.appendChild(art);
  box.appendChild(el('h1', 'ypw-empty__title', title));
  box.appendChild(el('p', 'ypw-empty__text', text));
  return box;
}

/* -------------------------------------------------------------------------
   /transaction/:type
   ------------------------------------------------------------------------- */

export function initHistoryPage(app, page, apiBase, fmt) {
  var type = sourceFor(page.route.params.type);
  var src = SOURCES[type];
  var root = page.el;
  var content = root.querySelector('.page-content');
  var listEl = root.querySelector('.ypw-txnlist ul');
  var holder = root.querySelector('.txn-history');
  var filtersEl = root.querySelector('.ypw-txnfilters');
  var cached = cachedList(type);
  /* Back from a transaction keeps the filter and the place in the list;
     arriving afresh starts at the top with everything */
  var returning = page.from === 'previous' && !!cached;
  var state = cached || {};
  if (!returning) {
    state.filter = 'all';
    state.scrollTop = 0;
  }

  root.querySelector('.txn-title').textContent = src.title;

  content.addEventListener('scroll', function () {
    if (lists[type]) lists[type].scrollTop = content.scrollTop;
  }, { passive: true });

  function paintFilters(models) {
    if (type !== 'wallet') return;
    var present = {};
    models.forEach(function (m) { present[m.group] = true; });
    var groups = GROUPS.filter(function (g) { return present[g.id]; });
    filtersEl.innerHTML = '';
    /* One kind of transaction only: nothing to filter */
    if (groups.length < 2) {
      filtersEl.classList.add('hidden');
      return;
    }
    if (state.filter !== 'all' && !present[state.filter]) state.filter = 'all';
    [{ id: 'all', label: 'All' }].concat(groups).forEach(function (g) {
      var chip = el('button', 'ypw-chip ypw-txnfilter' + (state.filter === g.id ? ' is-active' : ''), g.label);
      chip.setAttribute('type', 'button');
      chip.setAttribute('data-filter', g.id);
      chip.setAttribute('aria-pressed', state.filter === g.id ? 'true' : 'false');
      filtersEl.appendChild(chip);
    });
    filtersEl.classList.remove('hidden');
  }

  function paintList(models) {
    listEl.innerHTML = '';
    var old = holder.querySelector('.ypw-empty');
    if (old) old.remove();
    var shown = models.filter(function (m) { return state.filter === 'all' || m.group === state.filter; });
    if (!models.length) {
      holder.appendChild(emptyState(src.empty, 'Once you start moving money, it will show up here.'));
      return;
    }
    if (!shown.length) {
      holder.appendChild(emptyState('Nothing here', 'No transactions of this kind yet.'));
      return;
    }
    var frag = document.createDocumentFragment();
    shown.forEach(function (m) { frag.appendChild(rowFor(m)); });
    listEl.appendChild(frag);
  }

  function paint(fresh, keepScroll) {
    state = fresh;
    paintFilters(state.models);
    paintList(state.models);
    if (keepScroll) content.scrollTop = state.scrollTop || 0;
  }

  filtersEl.addEventListener('click', function (e) {
    var chip = e.target.closest('.ypw-txnfilter');
    if (!chip) return;
    state.filter = chip.getAttribute('data-filter');
    state.scrollTop = 0;
    paintFilters(state.models);
    paintList(state.models);
    content.scrollTop = 0;
    /* keep the chosen chip in view on a narrow screen */
    var active = filtersEl.querySelector('.is-active');
    if (active && active.scrollIntoView) {
      try { active.scrollIntoView({ inline: 'nearest', block: 'nearest' }); } catch (err) { /* older WebView */ }
    }
  });

  /* Placeholder rows while the first load is on its way. Not the app's
     modal "Loading..." dialog: a list should not block the screen, and
     app.dialog.close() closes whichever dialog is open -- on a cold start
     another page's spinner is, and one can be left stuck, invisible,
     swallowing taps. */
  function paintPlaceholders() {
    listEl.innerHTML = '';
    for (var i = 0; i < 6; i += 1) {
      var li = el('li', 'ypw-txn ypw-txn--placeholder');
      li.setAttribute('aria-hidden', 'true');
      li.innerHTML = '<span class="ypw-txn__link"><span class="ypw-txn__tile"></span>' +
        '<span class="ypw-txn__body"><span class="ypw-txn__bar"></span><span class="ypw-txn__bar ypw-txn__bar--short"></span></span>' +
        '<span class="ypw-txn__side"><span class="ypw-txn__bar ypw-txn__bar--amount"></span></span></span>';
      listEl.appendChild(li);
    }
  }

  var token = localStorage.getItem('authToken');
  if (cached) {
    lists[type] = state;
    paint(state, returning);
  } else {
    paintPlaceholders();
  }

  src.fetch(apiBase, token)
    .done(function (data) {
      if (!root.isConnected) return;
      if (data && data.status !== 'success' && data.msg && type !== 'wallet') {
        app.toast.show({ text: data.msg, closeTimeout: 3000 });
      }
      var before = cached ? cached.raw : null;
      var keep = { filter: state.filter, scrollTop: state.scrollTop };
      var fresh = remember(type, data, fmt);
      fresh.filter = keep.filter;
      fresh.scrollTop = keep.scrollTop;
      /* Unchanged: leave the list (and the customer's place in it) alone */
      if (before !== null && before === fresh.raw) return;
      paint(fresh, returning);
    })
    .fail(function () {
      if (cached || !root.isConnected) return;
      listEl.innerHTML = '';
      holder.appendChild(emptyState('Could not load your transactions', 'Check your connection and open this page again.'));
    });
}

/* -------------------------------------------------------------------------
   /txn/:key/
   ------------------------------------------------------------------------- */

function detailRow(label, value, copy) {
  var row = el('div', 'ypw-payee-card__row');
  row.appendChild(el('span', null, label));
  var b = el('b', null, value);
  if (copy) {
    var wrap = el('span', 'ypw-txnd__copyrow');
    wrap.appendChild(b);
    var btn = el('button', 'ypw-txnd__copy no-ripple');
    btn.setAttribute('type', 'button');
    btn.setAttribute('data-copy', value);
    btn.setAttribute('data-copy-label', label + ' copied');
    btn.setAttribute('aria-label', 'Copy ' + label.toLowerCase());
    btn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="11" height="11" rx="2.5"/><path d="M15 6.5A2.5 2.5 0 0 0 12.5 4h-6A2.5 2.5 0 0 0 4 6.5v6A2.5 2.5 0 0 0 6.5 15"/></svg>';
    wrap.appendChild(btn);
    row.appendChild(wrap);
  } else {
    row.appendChild(b);
  }
  return row;
}

function paintDetails(app, root, m) {
  root.querySelector('.ypw-txnd__body').classList.remove('hidden');
  root.querySelector('.ypw-txnd__missing').classList.add('hidden');

  var hero = root.querySelector('.ypw-txnd__hero');
  var oldTile = hero.querySelector('.ypw-txnd__tile');
  if (oldTile) oldTile.remove();
  hero.insertBefore(tileFor(m, 'ypw-txnd__tile'), hero.firstChild);
  root.querySelector('.ypw-txnd__title').textContent = m.title;
  var amount = root.querySelector('.ypw-txnd__amount');
  amount.textContent = m.amount;
  amount.className = 'ypw-txnd__amount ypw-txnd__amount--' + m.flow;
  var pill = root.querySelector('.ypw-txnd__status');
  pill.textContent = m.status.label;
  pill.className = 'ypw-txnd__status ypw-txn__status ypw-txn__status--' + m.status.kind;
  root.querySelector('.ypw-txnd__date').textContent = niceDate(m.date, true);

  var note = root.querySelector('.ypw-txnd__note');
  note.querySelector('.ypw-callout__text').textContent = m.note || '';
  note.classList.toggle('hidden', !m.note);

  /* Status and date are in the header above, so the card holds the rest */
  var rows = root.querySelector('.ypw-txnd__rows');
  rows.innerHTML = '';
  if (m.txnID) rows.appendChild(detailRow('Transaction ID', m.txnID, true));
  m.rows.forEach(function (r) { rows.appendChild(detailRow(r[0], r[1])); });
  rows.classList.toggle('hidden', !rows.children.length);

  var feeBox = root.querySelector('.ypw-txnd__fee');
  var feeDue = !!(m.fee && m.fee.payable);
  feeBox.classList.toggle('hidden', !m.fee);
  if (m.fee) {
    var feeRows = feeBox.querySelector('.ypw-txnd__feerows');
    feeRows.innerHTML = '';
    feeRows.appendChild(detailRow('Amount', m.fee.amount));
    feeRows.appendChild(detailRow('Status', m.fee.state));
    var pay = feeBox.querySelector('.ypw-txnd__pay');
    pay.classList.toggle('hidden', !feeDue);
    pay.setAttribute('href', '/fees/transaction/' + encodeURIComponent(m.fee.txnID));
  }

  /* One main action per screen: with a fee to pay, Repeat steps back */
  var repeat = root.querySelector('.ypw-txnd__repeat');
  repeat.classList.toggle('hidden', !m.repeat);
  repeat.classList.toggle('button-fill', !feeDue);
  repeat.classList.toggle('button-outline', feeDue);
  if (m.repeat) {
    repeat.querySelector('span').textContent = m.repeat.label;
    repeat.onclick = function (e) {
      e.preventDefault();
      giveRepeat(m.repeat.target, m.repeat.data);
      var view = app.views.main || app.views.current;
      view.router.navigate(m.repeat.route);
    };
  }
}

export function initTxnDetailsPage(app, page, apiBase, fmt) {
  var root = page.el;
  var key = decodeURIComponent(page.route.params.key || '');
  var m = byKey[key];

  function missing() {
    root.querySelector('.ypw-txnd__body').classList.add('hidden');
    root.querySelector('.ypw-txnd__missing').classList.remove('hidden');
  }

  if (m) {
    paintDetails(app, root, m);
    return;
  }
  /* Opened directly (a reload on this URL): wallet rows can be fetched again.
     The body stays hidden meanwhile -- no modal spinner, for the reason given
     in initHistoryPage. */
  if (key.charAt(0) !== 'w') {
    missing();
    return;
  }
  root.querySelector('.ypw-txnd__body').classList.add('hidden');
  SOURCES.wallet.fetch(apiBase, localStorage.getItem('authToken'))
    .done(function (data) {
      remember('wallet', data, fmt);
      if (byKey[key]) paintDetails(app, root, byKey[key]);
      else missing();
    })
    .fail(missing);
}
